/** @format */

import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	computed,
	inject,
	input,
	OnDestroy,
	output,
	signal
} from '@angular/core';
import {
	EventBusService,
	Events,
	MyAccountAnalyticsService,
	SnackbarService,
	Store
} from '@shared/data-access/services';

import {
	FavoritesActionResult,
	FavoritesIconEnum,
	FavoritesLocationEnum
} from '@shared/favorites/data-access/enums';
import { WishListService } from '@shared/favorites/data-access/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { ActivatedRoute } from '@angular/router';
import {
	FavoriteProduct,
	FavoritesActionDeterminationData
} from '@shared/favorites/data-access/interfaces';
import { CriteoService } from '@shared/data-access/product-recommendations';
import {
	HomefieldIconLibraryModule,
	homefieldIconReactionLoveFilled,
	homefieldIconReactionLoveOutlined,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { ProductStyle } from '@shared/data-access/interfaces';

@Component({
	selector: 'shared-favorites-components-auth-icon',
	templateUrl: './favorites-icon.component.html',
	styleUrl: './favorites-icon.component.scss',
	imports: [HomefieldIconLibraryModule]
})
export class FavoritesIconComponent implements AfterViewInit, OnDestroy {
	private readonly wishlistService = inject(WishListService);
	private readonly snackbarService = inject(SnackbarService);
	private readonly analyticsService = inject(MyAccountAnalyticsService);
	private readonly eventBusService = inject(EventBusService);
	private readonly store = inject(Store);
	private readonly changeDetector = inject(ChangeDetectorRef);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly criteoService = inject(CriteoService);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	readonly ecode = input<string | null>(null);
	readonly resolvedSku = input<string | null>(null);
	readonly location = input<FavoritesLocationEnum>(FavoritesLocationEnum.FAVORITES);
	readonly bundleProduct = input<ProductStyle | null>(null);
	readonly isAuthenticated = input<boolean>(false);
	readonly onWishlistBeacon = input<string>('');
	readonly favoritedECodes = input<string[]>([]);

	readonly product = input<FavoriteProduct | null>(null);

	readonly listName = input<string | null>(null);

	readonly listIndex = input<number | null>(null);

	deleteProductEmit = output<string>();

	readonly productsInStore = signal<Map<string, string[]>>(new Map());

	readonly productIsFavorited = computed(() => {
		if (this.location() === this.favoritesLocationEnum.FAVORITES) {
			return true;
		}

		const ecode = this.ecode();
		const skuOrEcode = this.resolvedSku() ? this.resolvedSku() : ecode;
		// If we're in FBT location, use externally provided favoritedECodes on initial load state
		// Use subsequent updates to productsInStore to drive the favorited UI state.
		if (this.location() === this.favoritesLocationEnum.FBT) {
			if (!this.productsInStore().get(skuOrEcode)) {
				return false;
			}
			return (
				!!this.productsInStore().get(skuOrEcode) ||
				this.favoritedECodes().includes(skuOrEcode)
			);
		}

		return !!this.productsInStore().get(skuOrEcode);
	});

	readonly iconName = computed(() =>
		this.productIsFavorited() ? FavoritesIconEnum.FILLED : FavoritesIconEnum.OUTLINED
	);
	readonly pdpButtonLabel = computed(() =>
		this.productIsFavorited() ? 'Delete product from list' : 'Add product to list'
	);
	readonly pdpButtonText = computed(() =>
		this.productIsFavorited() ? 'Added to List' : 'Add to List'
	);

	readonly actionInProgress = this.wishlistService.actionInProgress;

	private ngUnsubscribe: Subject<void> = new Subject();

	protected readonly favoritesLocationEnum = FavoritesLocationEnum;

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconReactionLoveFilled,
			homefieldIconReactionLoveOutlined
		]);
	}

	ngAfterViewInit(): void {
		this.store
			.select<Map<string, string[]>>('SAVEDPRODUCTS', true)
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe({
				next: (productsInStore) => {
					if (this.location() !== this.favoritesLocationEnum.FAVORITES) {
						this.productsInStore.set(new Map(productsInStore));
						this.changeDetector.detectChanges();
					}
				},
				error: (err) => {
					this.analyticsService.generateLogError(
						MyAccountErrorLogKeys.FAVORITES,
						'Failed to get favorites from store',
						err
					);
				}
			});
	}

	ngOnDestroy() {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
		this.eventBusService.unsubscribe(Events.AttributeClickEvent);
	}

	iconClicked() {
		const openQuickView =
			this.location() === this.favoritesLocationEnum.PLP ||
			this.location() === this.favoritesLocationEnum.RECOMMENDED;

		const favoritesActionData: FavoritesActionDeterminationData = {
			isAuthenticated: this.isAuthenticated(),
			ecode: this.ecode(),
			sku: this.resolvedSku(),
			productIsFavorited: this.productIsFavorited(),
			favoritesLocation: this.location(),
			productsInStore: this.productsInStore(),
			favoritedProductId: this.product()?.id,
			openQuickView,
			quickViewOptions: {
				referrer: location.pathname,
				additionalData:
					this.location() === this.favoritesLocationEnum.RECOMMENDED
						? {
								dsDisplayName:
									this.activatedRoute.snapshot.queryParams.dsDisplayName,
								dsDiversityLevel: '',
								dsPlacementId:
									this.activatedRoute.snapshot.queryParams.dsPlacementId,
								dsPosition: this.listIndex(),
								dsPriceRerankLevel:
									this.activatedRoute.snapshot.queryParams.dsPriceRerankLevel,
								dsRecsGCPProject: ''
							}
						: undefined
			}
		};

		if (this.onWishlistBeacon()) {
			favoritesActionData.onWishlistBeacon = () =>
				this.criteoService.fireBeacon(this.onWishlistBeacon());
		}

		this.wishlistService.determineFavoritesAction(favoritesActionData).subscribe((result) => {
			if (result === FavoritesActionResult.REMOVED) {
				this.deleteProductEmit.emit(this.product().id);
				this.snackbarService.showSnackBar({
					isAuthSnackbar: false,
					showSnackbar: true,
					text: `Removed from ${this.listName()}`,
					link: ''
				});

				this.analyticsService.triggerAnalyticsEvent('RemoveFromList', {
					NumberOfLists: 1,
					ProductID: this.ecode(),
					ProductSku: this.resolvedSku()
				});
				return;
			}
			if (result === FavoritesActionResult.REMOVAL_ERROR) {
				this.snackbarService.showSnackBar({
					isAuthSnackbar: false,
					showSnackbar: true,
					text: 'Unexpected error. Please try again later.',
					link: this.location() === 'Favorites' ? '' : '/MyAccount/Favorites'
				});
				this.analyticsService.singleErrorToAnalytics('RemoveFromFavorites_Error');
			}
		});
	}
}
